import { actions } from 'state/current-user/current-user.slice';

import { changeUserPassword } from 'api/generated/users/users';

import Notification from 'components/partials/notification/notification.helper';

import type { AppThunk } from 'state/store';

import { getOktaUserId } from 'helpers/okta-session';

const { changingPassword, changingPasswordFailed, changingPasswordSucceeded } =
  actions;

interface ChangePasswordArgs {
  newPassword: string;
  oldPassword: string;
}

interface ChangePasswordError {
  status?: number;
  data?: {
    detail?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export const changePasswordAction =
  ({
    newPassword,
    oldPassword,
  }: ChangePasswordArgs): AppThunk<Promise<unknown>> =>
  (dispatch) => {
    dispatch(changingPassword());

    const oktaUserId = getOktaUserId();

    if (!oktaUserId) {
      dispatch(changingPasswordFailed());

      // Wrap in Error so the rejection reason is Error-shaped (Sonar
      // typescript:S6671) while still carrying the legacy `status`/`data`
      // fields consumers rely on.
      const err = Object.assign(new Error('Not authenticated'), {
        status: undefined,
        data: { detail: 'Not authenticated' },
      }) as Error & ChangePasswordError;

      return Promise.reject(err);
    }

    return changeUserPassword(oktaUserId, {
      newPassword,
      oldPassword,
    })
      .then((response: unknown) => {
        dispatch(changingPasswordSucceeded());

        Notification.flash(
          `Password changed`,
          'success',
          `You've successfully updated your password`,
        );

        return response;
      })
      .catch((error: ChangePasswordError) => {
        dispatch(changingPasswordFailed());

        let addtlDetails = '';

        if (error.data && error.data.detail) {
          addtlDetails = ': ' + error.data.detail;
        }

        if (error && error.status !== 401) {
          Notification.flash(
            `Password change failed`,
            'error',
            `An error occurred when changing your password${addtlDetails}`,
          );
        }

        // Wrap in an Error so the rejection reason is always Error-shaped while
        // still preserving the original `status`/`data` fields for callers.
        const wrapped = Object.assign(
          new Error(
            error?.data?.detail ?? `Password change failed (${error?.status ?? 'unknown'})`,
          ),
          error,
        );

        return Promise.reject(wrapped);
      });
  };
